/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { CreateUpdateBaseComponent } from 'app/shared/components/create-update-base/create-update-base.component';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { ApiResponseSummary } from 'app/shared/models/api-info.model';
import { timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { RefundOption } from 'app/shared/enumeration/refund-option';
import { RefundType } from 'app/shared/enumeration/refund-type';
import { ConfirmationDialogService } from 'app/shared/services/confirmation-dialog.service';
import { RefundFormModel } from '../models/refund-form.model';
import { RefundTicketPriceTypeListComponent } from '../refund-ticket-price-type-list/refund-ticket-price-type-list.component';
import { RefundFormService } from '../services/refund-form.service';
import { SnackBarService } from 'app/shared/services/snack-bar.service';
import { RefundProcessingStatus } from 'app/shared/enumeration/refund-processing-status';

const atLeastOneRefundSelectionValidator = (refundDetails?: RefundFormModel): ValidatorFn =>
	(control: AbstractControl): ValidationErrors | null => {
		let customerRefundTotal = 0;
		let retainedRefundTotal = 0;
		const addRefundAmount = (refundControl: AbstractControl | null, amount: any) => {
			if (!refundControl?.enabled) {
				return;
			}

			const refundAmount = Number(amount) || 0;
			if (refundControl.value === RefundType.Customer) {
				customerRefundTotal += refundAmount;
			} else if (refundControl.value === RefundType.Retained) {
				retainedRefundTotal += refundAmount;
			}
		};

		addRefundAmount(control.get('processingFeeRefundType'), refundDetails?.processingFeePaid);
		addRefundAmount(control.get('dispatchMethodRefundType'), refundDetails?.dispatchMethodFeePaid);
		addRefundAmount(control.get('cancellationProtectionRefundType'), refundDetails?.cancellationProtectionFeePaid);
		addRefundAmount(control.get('charityDonationRefundType'), refundDetails?.charityDonationAmountPaid);

		const ticketArrays = [control.get('payInFullTickets'), control.get('paymentPlanTickets')];
		ticketArrays.forEach((ticketArray) =>
			(ticketArray as FormArray)?.controls.forEach((ticketGroup) => {
				addRefundAmount(ticketGroup.get('ticketRefundType'), ticketGroup.get('ticketPrice')?.value);
				addRefundAmount(ticketGroup.get('bookingFeeRefundType'), ticketGroup.get('bookingFee')?.value);
				addRefundAmount(ticketGroup.get('venueLevyFeeRefundType'), ticketGroup.get('venueLevy')?.value);
			})
		);

		const hasRefundAmount = Math.round(customerRefundTotal * 100) / 100 > 0
			|| Math.round(retainedRefundTotal * 100) / 100 > 0;
		return hasRefundAmount ? null : { refundSelectionRequired: true };
	};

@Component({
	selector: 'refund-form',
	templateUrl: './refund-form.component.html',
	styleUrls: ['./refund-form.component.scss'],
	standalone: false,
})
export class RefundFormComponent extends CreateUpdateBaseComponent<RefundFormModel> implements OnInit, OnDestroy {
	refundOptionLookupData: any[];
	refundTypeLookupData: any[];
	dataModel: RefundFormModel;
	isSubmitting = false;
	refundPending = false;
	private refundStatusPollTimer: ReturnType<typeof setTimeout>;
	private refundStatusPollAttempt = 0;
	private destroyed = false;

	@ViewChild('payInFullTickets') payInFullTicketsComponent: RefundTicketPriceTypeListComponent;
	@ViewChild('paymentPlanTickets') paymentPlanTicketsComponent: RefundTicketPriceTypeListComponent;

	displayedColumns: string[] = [
		'ticketType',
		'ticketNumber',
		'ticketPrice',
		'ticketPricePaid',
		'ticketRefundType',
		'bookingFee',
		'bookingFeePaid',
		'bookingFeeRefundType',
		'venueLevy',
		'venueLevyPaid',
		'venueLevyFeeRefundType',
		'paymentStatus',
		'payInFullTicketstatus',
		'dispatchStatus',
	];
	constructor(protected fb: FormBuilder, private service: RefundFormService, protected asideService: AsideService, protected formHelperService: ReactiveFormHelperService, protected confirmationDialogService: ConfirmationDialogService, protected snackBarService: SnackBarService) {
		super(fb, asideService, formHelperService, confirmationDialogService);
	}

	// shortcuts to controls for readability
	get refundOption(): AbstractControl {
		return this.dataForm.controls['refundOption'];
	}

	get processingFeeRefundType(): AbstractControl {
		return this.dataForm.controls['processingFeeRefundType'];
	}

	get dispatchMethodRefundType(): AbstractControl {
		return this.dataForm.controls['dispatchMethodRefundType'];
	}

	get cancellationProtectionRefundType(): AbstractControl {
		return this.dataForm.controls['cancellationProtectionRefundType'];
	}

	get charityDonationRefundType(): AbstractControl {
		return this.dataForm.controls['charityDonationRefundType'];
	}

	get payInFullTicketsFormArray(): FormArray {
		return this.dataForm.get('payInFullTickets') as FormArray;
	}

	get paymentPlanTicketsFormArray(): FormArray {
		return this.dataForm.get('paymentPlanTickets') as FormArray;
	}

	// overidden from CreateUpdateBaseComponent base class
	protected createFormGroup(): FormGroup<any> {
		return this.fb.group({
			refundOption: [RefundOption.ClearAllRefunds],
			processingFeeRefundType: [RefundType.NotRefunded],
			dispatchMethodRefundType: [RefundType.NotRefunded],
			cancellationProtectionRefundType: [RefundType.NotRefunded],
			charityDonationRefundType: [RefundType.NotRefunded],
			payInFullTickets: this.fb.array([]),
			paymentPlanTickets: this.fb.array([]),
		}, { validators: atLeastOneRefundSelectionValidator() });
	}

	// overidden from CreateUpdateBaseComponent base class
	protected populateControls(datafrom: FormGroup<any>) {
		this.refundOptionLookupData = this.formHelperService.createLookupData(this.viewModel.refundOptionLookupData);
		this.refundTypeLookupData = this.formHelperService.createLookupData(this.viewModel.refundTypeLookupData);

		this.dataForm = this.fb.group({
			refundOption: [RefundOption.ClearAllRefunds],
			processingFeeRefundType: [this.viewModel.processingFeeRefundType],
			dispatchMethodRefundType: [this.viewModel.dispatchMethodRefundType],
			cancellationProtectionRefundType: [this.viewModel.cancellationProtectionRefundType],
			charityDonationRefundType: [this.viewModel.charityDonationRefundType],
			payInFullTickets: this.fb.array(this.viewModel.payInFullTickets.map((ticket) => this.createTicketForm(ticket))),
			paymentPlanTickets: this.fb.array(this.viewModel.paymentPlanTickets.map((ticket) => this.createTicketForm(ticket))),
		}, { validators: atLeastOneRefundSelectionValidator(this.viewModel) });
		this.refundOption.setValue(this.viewModel.refundOption);

		this.dataForm.valueChanges
			.pipe(
				switchMap(() => timer(500)) // wait 500ms after last change
			)
			.subscribe(() => {
				this.calculateRefundTotals();
			});
		this.refundOption?.valueChanges.subscribe((value) => {
			this.setRefundTypeDefault(value);
		});
		this.disablePreviouslyRefundedControls();

		if (this.viewModel.pendingRefundId) {
			this.refundPending = true;
			this.dataForm.disable({ emitEvent: false });
			this.scheduleRefundStatusPoll(this.viewModel.pendingRefundId);
		}
	}
	private disablePreviouslyRefundedControls() {
		if (this.viewModel.processingFeeRefundType == RefundType.Customer || this.viewModel.processingFeeRefundType == RefundType.Retained) {
			this.processingFeeRefundType.disable();
		}
		if (this.viewModel.dispatchMethodRefundType == RefundType.Customer || this.viewModel.dispatchMethodRefundType == RefundType.Retained) {
			this.dispatchMethodRefundType.disable();
		}
		if (this.viewModel.cancellationProtectionRefundType == RefundType.Customer || this.viewModel.cancellationProtectionRefundType == RefundType.Retained) {
			this.cancellationProtectionRefundType.disable();
		}
		if (this.viewModel.charityDonationRefundType == RefundType.Customer || this.viewModel.charityDonationRefundType == RefundType.Retained) {
			this.charityDonationRefundType.disable();
		}
	}

	private setRefundTypeDefault(value: any) {
		var refundType = null;
		if (value === RefundOption.FullCustomerRefund) {
			refundType = RefundType.Customer;
		} else if (value === RefundOption.FullInternalRefund) {
			refundType = RefundType.Retained;
		} else if (value === RefundOption.ClearAllRefunds) {
			refundType = RefundType.NotRefunded;
		}

		this.payInFullTicketsComponent?.setRefundTypeControls(refundType);
		this.paymentPlanTicketsComponent?.setRefundTypeControls(refundType);

		if (this.processingFeeRefundType.enabled) {
			this.processingFeeRefundType.setValue(refundType);
		}
		if (this.dispatchMethodRefundType.enabled) {
			this.dispatchMethodRefundType.setValue(refundType);
		}
		if (this.cancellationProtectionRefundType.enabled) {
			this.cancellationProtectionRefundType.setValue(refundType);
		}
		if (this.charityDonationRefundType.enabled) {
			this.charityDonationRefundType.setValue(refundType);
		}
	}

	calculateRefundTotals() {
		var model = this.createUpdateModel();

		this.service.calculateRefundTotals(this.dataModel.id, model).then((response: RefundFormModel) => {
			this.dataModel.customerRefundTotal = response.customerRefundTotal;
			this.dataModel.refundPaymentAmount = response.refundPaymentAmount;
			this.dataModel.retainedRefundTotal = response.retainedRefundTotal;
			this.dataModel.proposedOrderTotal = response.proposedOrderTotal;
			this.dataModel.proposedBalanceOutstanding = response.proposedBalanceOutstanding;
			this.dataModel.proposedRemainingInstalments = response.proposedRemainingInstalments;
		});
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async loadData(id: number): Promise<RefundFormModel> {
		//let dataModel: RefundFormModel;

		await this.service.getRefundForm(id).then((response: RefundFormModel) => {
			this.dataModel = response;
		});

		return Promise.resolve(this.dataModel);
	}

	createTicketForm(ticket: any): FormGroup {
		return this.fb.group({
			id: [ticket.id],
			ticketType: [ticket.ticketType],
			ticketNumber: [ticket.ticketNumber],
			ticketPrice: [ticket.ticketPrice],
			ticketPricePaid: [ticket.ticketPricePaid],
			ticketRefundType: [ticket.ticketRefundType ?? RefundType.NotRefunded],
			bookingFee: [ticket.bookingFee],
			bookingFeePaid: [ticket.bookingFeePaid],
			bookingFeeRefundType: [ticket.bookingFeeRefundType ?? RefundType.NotRefunded],
			venueLevy: [ticket.venueLevy],
			venueLevyPaid: [ticket.venueLevyPaid],
			venueLevyFeeRefundType: [ticket.venueLevyFeeRefundType ?? RefundType.NotRefunded],
			paymentStatus: [ticket.paymentStatus],
			payInFullTicketStatus: [ticket.payInFullTicketStatus],
			dispatchStatus: [ticket.dispatchStatus],
			ticketStatus: [ticket.ticketStatus],
			canRefund: [ticket.canRefund],
		});
	}

	// overidden from CreateUpdateBaseComponent base class
	protected createUpdateModel() {
		const extractRefundTypes = (tickets: any[]) =>
			tickets.map(({ id, ticketRefundType, bookingFeeRefundType, venueLevyFeeRefundType }) => ({
				id,
				ticketRefundType,
				bookingFeeRefundType,
				venueLevyFeeRefundType,
			}));

		return {
			payInFullTickets: extractRefundTypes(this.payInFullTicketsFormArray.value),
			paymentPlanTickets: extractRefundTypes(this.paymentPlanTicketsFormArray.value),
			processingFeeRefundType: this.processingFeeRefundType.disabled ? null : this.processingFeeRefundType.value,
			dispatchMethodRefundType: this.dispatchMethodRefundType.disabled ? null : this.dispatchMethodRefundType.value,
			cancellationProtectionRefundType: this.cancellationProtectionRefundType.disabled ? null : this.cancellationProtectionRefundType.value,
			charityDonationRefundType: this.charityDonationRefundType.disabled ? null : this.charityDonationRefundType.value,
			timestamp: this.viewModel ? this.viewModel.timestamp : null,
		};
	}

	protected async doUpdate(id: number, dataToUpdate: any): Promise<ApiResponseSummary<any>> {
		let updateResponse: ApiResponseSummary<any>;

		await this.service.createRefund(id, dataToUpdate).then((response: ApiResponseSummary<any>) => {
			updateResponse = response;
		});

		return Promise.resolve(updateResponse);
	}

	// overidden from CreateUpdateBaseComponent base class
	save() {
		if (this.isSubmitting || this.refundPending) {
			return;
		}

		if (this.formHelperService.isFormValid(this.dataForm)) {
			this.isSubmitting = true;
			const updateModel = this.createUpdateModel();
			this.doCreate(updateModel)
				.then((response: any) => {
					const status = response.data?.status as RefundProcessingStatus;
					const message = response.data?.message;

					if (status === RefundProcessingStatus.Success) {
						if (message) {
							this.snackBarService.info(message);
						}
						this.asideService.close('update');
						return;
					}

					if (status === RefundProcessingStatus.Pending) {
						this.refundPending = true;
						this.dataForm.disable({ emitEvent: false });
						if (message) {
							this.snackBarService.info(message);
						}
						this.scheduleRefundStatusPoll(response.data?.refundId);
						return;
					}

					this.snackBarService.warning(message ?? 'Refund failed');
				})
				.finally(() => {
					this.isSubmitting = false;
				});
		}
	}

	cancel() {
		if (this.refundPending) {
			this.asideService.close('update');
			return;
		}

		super.cancel();
	}

	ngOnDestroy(): void {
		this.destroyed = true;
		if (this.refundStatusPollTimer) {
			clearTimeout(this.refundStatusPollTimer);
		}
	}

	private scheduleRefundStatusPoll(refundId: number): void {
		if (!refundId || this.destroyed || !this.refundPending) {
			return;
		}

		const delay = Math.min(2000 + this.refundStatusPollAttempt * 500, 10000);
		this.refundStatusPollTimer = setTimeout(
			() => this.pollRefundStatus(refundId),
			delay
		);
	}

	private async pollRefundStatus(refundId: number): Promise<void> {
		if (this.destroyed || !this.refundPending) {
			return;
		}

		try {
			const response = await this.service.reconcileRefund(this.dataModel.id, refundId);
			const result: any = response?.data;
			const status = result?.status as RefundProcessingStatus;

			if (status === RefundProcessingStatus.Success && result?.refundFinalisedDate) {
				this.refundPending = false;
				this.snackBarService.info(result.message ?? 'Successfully Refunded');
				this.asideService.close('update');
				return;
			}

			if (status === RefundProcessingStatus.Failed || status === RefundProcessingStatus.Partial) {
				this.refundPending = false;
				this.dataForm.enable({ emitEvent: false });
				this.disablePreviouslyRefundedControls();
				this.payInFullTicketsComponent?.disableControlsIfCustomerRefundType();
				this.paymentPlanTicketsComponent?.disableControlsIfCustomerRefundType();
				this.snackBarService.warning(result?.message ?? 'Refund failed');
				return;
			}
		} catch {
			// A polling failure does not establish the provider outcome. Keep the form
			// pending and try the same persisted refund attempt again.
		}

		this.refundStatusPollAttempt++;
		this.scheduleRefundStatusPoll(refundId);
	}

	protected async doCreate(dataToUpdate: any): Promise<ApiResponseSummary<any>> {
		return await this.service.createRefund(this.dataModel.id, dataToUpdate);
	}
}
